import { z } from 'zod';
import {
  IdSchema,
  JsonValueSchema,
  NonEmptyStringSchema,
  OptionalNullableStringSchema,
  TimestampSchema,
} from './common';
import { funnelOverviewSummarySchema } from './funnel-overview-summary';

const JsonObjectSchema = z.record(JsonValueSchema);
const LooseStatusSchema = NonEmptyStringSchema;
const GeneratedAtFieldsSchema = z.object({
  generatedAt: TimestampSchema.optional(),
  generated_at: TimestampSchema.optional(),
});
const RunIdFieldsSchema = z.object({
  id: IdSchema.optional(),
  runId: IdSchema.optional(),
  run_id: IdSchema.optional(),
  analysisRunId: IdSchema.optional(),
  analysis_run_id: IdSchema.optional(),
});
const BuildIdFieldsSchema = z.object({
  buildId: IdSchema.optional(),
  build_id: IdSchema.optional(),
});
const ProposalHashFieldsSchema = z.object({
  proposalHash: IdSchema.optional(),
  proposal_hash: IdSchema.optional(),
});
const HumanApprovalFieldsSchema = z.object({
  humanApprovalRef: OptionalNullableStringSchema,
  human_approval_ref: OptionalNullableStringSchema,
});

export const FunnelOverviewLicenseVerdictSchema = z.enum([
  'safe',
  'review_required',
  'blocked',
]);

export const FunnelOverviewAssetBindingModeSchema = z.enum([
  'embedded',
  'referenced',
]);

export const FunnelOverviewBrandResolutionSchema = z.enum([
  'observed',
  'inferred',
]);

export const FunnelOverviewBrandSourceSchema = z.enum([
  'structured_data',
  'product_brand',
  'open_graph',
  'application_metadata',
  'site_identity',
  'title',
  'hostname',
]);

export const FunnelOverviewBrandObservationSchema = z
  .object({
    name: NonEmptyStringSchema,
    resolution: FunnelOverviewBrandResolutionSchema,
    source: FunnelOverviewBrandSourceSchema,
    confidence: z.number().min(0).max(1),
    evidenceRefs: z.array(NonEmptyStringSchema).min(1),
  })
  .passthrough()
  .superRefine((observation, context) => {
    if (observation.source === 'hostname' && observation.resolution !== 'inferred') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['resolution'],
        message: 'hostname brand observations must be inferred',
      });
    }
    if (observation.source !== 'hostname' && observation.resolution !== 'observed') {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['resolution'],
        message: 'captured brand sources must be observed',
      });
    }
  });

export const FunnelOverviewEvidenceMediaKindSchema = z.enum([
  'page_screenshot',
  'component_crop',
  'source_asset',
]);

export const FunnelOverviewEvidenceMediaBindingSchema = z
  .object({
    assetId: IdSchema,
    kind: FunnelOverviewEvidenceMediaKindSchema,
    mediaType: NonEmptyStringSchema,
    pageRefs: z.array(NonEmptyStringSchema).default([]),
    evidenceRefs: z.array(NonEmptyStringSchema).default([]),
    subjectRefs: z.array(NonEmptyStringSchema).min(1),
    alt: NonEmptyStringSchema,
  })
  .passthrough();

export const FunnelOverviewAssetManifestEntrySchema = z
  .object({
    id: IdSchema,
    assetId: IdSchema.optional(),
    asset_id: IdSchema.optional(),
    sha256: NonEmptyStringSchema.optional(),
    mediaType: NonEmptyStringSchema.optional(),
    media_type: NonEmptyStringSchema.optional(),
    category: NonEmptyStringSchema,
    sourceUrl: OptionalNullableStringSchema,
    source_url: OptionalNullableStringSchema,
    pageRefs: z.array(NonEmptyStringSchema).optional(),
    page_refs: z.array(NonEmptyStringSchema).optional(),
    mode: FunnelOverviewAssetBindingModeSchema.optional(),
    storageMode: FunnelOverviewAssetBindingModeSchema.optional(),
    storage_mode: FunnelOverviewAssetBindingModeSchema.optional(),
    licenseRisk: FunnelOverviewLicenseVerdictSchema.optional(),
    license_risk: FunnelOverviewLicenseVerdictSchema.optional(),
    licenseVerdict: FunnelOverviewLicenseVerdictSchema.optional(),
    license_verdict: FunnelOverviewLicenseVerdictSchema.optional(),
    reason: OptionalNullableStringSchema,
    width: z.number().int().positive().nullable().optional(),
    height: z.number().int().positive().nullable().optional(),
    byteSize: z.number().int().nonnegative().optional(),
    byte_size: z.number().int().nonnegative().optional(),
  })
  .merge(HumanApprovalFieldsSchema)
  .passthrough();

export const FunnelOverviewFrameworkTypeSchema = z
  .object({
    id: IdSchema,
    label: NonEmptyStringSchema,
    description: z.string().optional(),
    rules: z.array(IdSchema).optional(),
  })
  .passthrough();

export const FunnelOverviewFrameworkRuleSchema = z
  .object({
    id: IdSchema,
    label: NonEmptyStringSchema.optional(),
    name: NonEmptyStringSchema.optional(),
    description: z.string().optional(),
    severity: NonEmptyStringSchema.optional(),
  })
  .passthrough();

export const FunnelOverviewFrameworkRegistryResponseSchema = z
  .object({
    version: NonEmptyStringSchema,
    contentHash: NonEmptyStringSchema.optional(),
    content_hash: NonEmptyStringSchema.optional(),
    types: z.array(FunnelOverviewFrameworkTypeSchema).default([]),
    rules: z.array(FunnelOverviewFrameworkRuleSchema).optional(),
  })
  .merge(GeneratedAtFieldsSchema)
  .passthrough();

export const FunnelOverviewTemplateCustomizationSlotSchema = z
  .object({
    id: IdSchema,
    slot: IdSchema.optional(),
    label: NonEmptyStringSchema.optional(),
    description: z.string().optional(),
    acceptedCategories: z.array(NonEmptyStringSchema).optional(),
    accepted_categories: z.array(NonEmptyStringSchema).optional(),
    cardinality: NonEmptyStringSchema.optional(),
    required: z.boolean().optional(),
  })
  .passthrough();

export const FunnelOverviewTemplateCustomizationSlotRegistryResponseSchema = z
  .object({
    version: NonEmptyStringSchema,
    contentHash: NonEmptyStringSchema.optional(),
    content_hash: NonEmptyStringSchema.optional(),
    slots: z.array(FunnelOverviewTemplateCustomizationSlotSchema).default([]),
  })
  .merge(GeneratedAtFieldsSchema)
  .passthrough();

export const FunnelOverviewSeedProductSchema = z
  .object({
    id: IdSchema,
    name: NonEmptyStringSchema,
    title: NonEmptyStringSchema,
    slug: NonEmptyStringSchema,
    url: NonEmptyStringSchema,
    price: z.number().nullable().optional(),
    currency: NonEmptyStringSchema.optional(),
    priceCurrency: NonEmptyStringSchema.optional(),
    images: z.array(NonEmptyStringSchema).optional(),
    image: OptionalNullableStringSchema,
    variants: z.array(NonEmptyStringSchema).optional(),
    description: z.string().optional(),
    category: z.string().optional(),
    brand: OptionalNullableStringSchema,
    sku: OptionalNullableStringSchema,
    gtin: OptionalNullableStringSchema,
    weight: OptionalNullableStringSchema,
    dimensions: OptionalNullableStringSchema,
    availability: OptionalNullableStringSchema,
  })
  .passthrough();

export const FunnelOverviewSeedCollectionSchema = z
  .object({
    slug: NonEmptyStringSchema,
    name: NonEmptyStringSchema,
    title: NonEmptyStringSchema,
    url: NonEmptyStringSchema,
    description: z.string().optional(),
    products: z.array(NonEmptyStringSchema).default([]),
    filters: z.array(JsonObjectSchema).default([]),
  })
  .passthrough();

export const FunnelOverviewSeedCartSchema = z
  .object({
    cartMode: NonEmptyStringSchema,
    layout: JsonObjectSchema.optional(),
    fields: z.array(NonEmptyStringSchema).default([]),
    cta: JsonObjectSchema.optional(),
    items: z
      .array(
        z
          .object({
            productId: IdSchema,
            quantity: z.number().int().positive(),
          })
          .passthrough(),
      )
      .default([]),
    diagnostics: z.array(z.string()).default([]),
  })
  .passthrough();

export const FunnelOverviewSeedFunnelStepSchema = z
  .object({
    nodeId: IdSchema,
    pageUrl: NonEmptyStringSchema,
    pageSlug: NonEmptyStringSchema,
    stepType: NonEmptyStringSchema,
    captured: z.boolean(),
  })
  .passthrough();

export const FunnelOverviewSeedFunnelSchema = z
  .object({
    id: IdSchema,
    type: NonEmptyStringSchema,
    steps: z.array(FunnelOverviewSeedFunnelStepSchema).default([]),
    offers: z.array(NonEmptyStringSchema).default([]),
  })
  .passthrough();

export const FunnelOverviewSeedFunnelsSchema = z
  .object({
    version: NonEmptyStringSchema,
    funnels: z.array(FunnelOverviewSeedFunnelSchema).default([]),
  })
  .passthrough();

export const FunnelOverviewSeedOfferSchema = z
  .object({
    id: IdSchema,
    productId: OptionalNullableStringSchema,
    product_handle: OptionalNullableStringSchema,
    type: NonEmptyStringSchema,
    value: OptionalNullableStringSchema,
    discount_type: OptionalNullableStringSchema,
    discount_value: z.number().nullable().optional(),
    _source: NonEmptyStringSchema.optional(),
    conditions: JsonObjectSchema.optional(),
  })
  .passthrough();

export const FunnelOverviewSeedArtifactsSchema = z
  .object({
    products: z.array(FunnelOverviewSeedProductSchema).default([]),
    collections: z.array(FunnelOverviewSeedCollectionSchema).default([]),
    cart: FunnelOverviewSeedCartSchema,
    funnels: FunnelOverviewSeedFunnelsSchema,
    offers: z.array(FunnelOverviewSeedOfferSchema).default([]),
  })
  .passthrough();

export const FunnelOverviewSeedsResponseSchema = z
  .object({
    version: NonEmptyStringSchema.optional(),
    dataFiles: z.record(NonEmptyStringSchema).optional(),
    data_files: z.record(NonEmptyStringSchema).optional(),
    seeds: z.union([
      FunnelOverviewSeedArtifactsSchema,
      z.array(JsonValueSchema),
    ]),
    diagnostics: z.array(z.string()).optional(),
  })
  .passthrough();

export const FunnelOverviewGapSchema = z
  .object({
    field: NonEmptyStringSchema,
    reason: NonEmptyStringSchema,
    evidenceRefs: z.array(NonEmptyStringSchema).optional(),
  })
  .passthrough();

export const FunnelOverviewClassificationSchema = z
  .object({
    trafileaFunnelType: NonEmptyStringSchema,
    frameworkType: z
      .object({
        id: z.number().int().nonnegative(),
        label: NonEmptyStringSchema,
      })
      .passthrough(),
    pageSpecies: NonEmptyStringSchema.optional(),
    trafficContext: JsonObjectSchema.optional(),
    confidence: z.number().min(0).max(1),
    detectionMethod: NonEmptyStringSchema,
    stepsCompleteness: z.number().min(0).max(1),
    partial: z.boolean().optional(),
    partialReason: NonEmptyStringSchema.optional(),
  })
  .passthrough();

export const FunnelOverviewObservedFunnelSchema = z
  .object({
    id: IdSchema,
    type: NonEmptyStringSchema,
    entry: z
      .object({
        nodeId: IdSchema,
        url: NonEmptyStringSchema,
        stepType: z.string().nullable(),
        captured: z.boolean(),
      })
      .passthrough()
      .nullable(),
    nodeIds: z.array(IdSchema),
    edgeIds: z.array(IdSchema),
    confidence: z.number().min(0).max(1),
    captureStatus: z.enum(['complete', 'partial', 'minimal', 'none']),
    missingSteps: z.array(NonEmptyStringSchema),
    evidenceRefs: z.array(NonEmptyStringSchema).min(1),
    evidenceAssetIds: z.array(IdSchema).optional(),
    unknowns: z.array(FunnelOverviewGapSchema),
  })
  .passthrough();

export const FunnelOverviewQualitySchema = z
  .object({
    status: z.enum(['complete', 'partial', 'degraded', 'failed']),
    reasons: z.array(NonEmptyStringSchema),
    strategicFieldCount: z.number().int().nonnegative(),
    structuralAuditFindingCount: z.number().int().nonnegative(),
    signals: z
      .object({
        hasConversionRisk: z.boolean(),
        hasProofMechanism: z.boolean(),
        hasCtaRecommendation: z.boolean(),
        hasPlaceholderData: z.boolean(),
        llmSourced: z.boolean(),
      })
      .passthrough(),
  })
  .passthrough();

export const FunnelOverviewSpineComponentRefSchema = z
  .object({
    id: IdSchema,
    name: NonEmptyStringSchema,
  })
  .passthrough();

export const FunnelOverviewSpineWinnerCombinationSchema = z
  .object({
    id: IdSchema,
    name: NonEmptyStringSchema,
    description: z.string(),
    componentIds: z.array(IdSchema),
    observedComponentIds: z.array(IdSchema),
    missingComponentIds: z.array(IdSchema),
    complete: z.boolean(),
    evidence: z.array(NonEmptyStringSchema),
  })
  .passthrough();

export const FunnelOverviewSpineAuditSchema = z
  .object({
    status: z.enum(['ready', 'insufficient_evidence']),
    orderedComponentIds: z.array(IdSchema),
    observedComponentIds: z.array(IdSchema),
    missingComponentIds: z.array(IdSchema),
    outOfOrderComponentIds: z.array(IdSchema),
    missingComponents: z.array(FunnelOverviewSpineComponentRefSchema),
    winnerCombinations: z
      .array(FunnelOverviewSpineWinnerCombinationSchema)
      .optional(),
    complete: z.boolean(),
    orderValid: z.boolean(),
    capsMaxScore: z.boolean(),
    scoreCap: z.number().min(0).max(100).nullable(),
    evidence: z.array(NonEmptyStringSchema),
  })
  .passthrough();

export const FunnelOverviewStructuralAuditFindingSchema = z
  .object({
    id: IdSchema.optional(),
    ruleId: IdSchema.optional(),
    rule_id: IdSchema.optional(),
    name: NonEmptyStringSchema.optional(),
    severity: NonEmptyStringSchema.optional(),
    status: NonEmptyStringSchema.optional(),
    evidence: z.array(NonEmptyStringSchema).optional(),
    evidenceRefs: z.array(NonEmptyStringSchema).optional(),
    evidence_refs: z.array(NonEmptyStringSchema).optional(),
    evidenceAssetIds: z.array(IdSchema).optional(),
    pageSlug: NonEmptyStringSchema.optional(),
  })
  .passthrough()
  .refine(
    (finding) =>
      finding.id !== undefined ||
      finding.ruleId !== undefined ||
      finding.rule_id !== undefined,
    {
      message: 'A structural finding requires id, ruleId, or rule_id',
    },
  );

export const FunnelOverviewStructuralAuditSchema = z
  .object({
    findings: z.array(FunnelOverviewStructuralAuditFindingSchema).default([]),
    ruleCount: z.number().int().nonnegative().optional(),
    rule_count: z.number().int().nonnegative().optional(),
    passCount: z.number().int().nonnegative().optional(),
    pass_count: z.number().int().nonnegative().optional(),
    warningCount: z.number().int().nonnegative().optional(),
    warning_count: z.number().int().nonnegative().optional(),
    failureCount: z.number().int().nonnegative().optional(),
    failure_count: z.number().int().nonnegative().optional(),
    rulesEvaluated: z.number().int().nonnegative().optional(),
    applicableRuleIds: z.array(IdSchema).optional(),
    taxonomyApplicableRuleIds: z.array(IdSchema).optional(),
    taxonomyNotEvaluatedRuleIds: z.array(IdSchema).optional(),
    passed: z.number().int().nonnegative().optional(),
    warnings: z.number().int().nonnegative().optional(),
    failureModes: z.number().int().nonnegative().optional(),
    spine: FunnelOverviewSpineAuditSchema.optional(),
  })
  .passthrough();

const FunnelOverviewStructuralAuditCompatibilitySchema = z.union([
  FunnelOverviewStructuralAuditSchema,
  z.array(FunnelOverviewStructuralAuditFindingSchema),
]);

export const FunnelOverviewCroScorecardStatusSchema = z.enum([
  'scored',
  'not_evaluated',
  'rubric_unavailable',
]);

export const FunnelOverviewCroRuleResultSchema = z.enum([
  'pass',
  'warning',
  'failure_mode',
  'not_observed',
]);

export const FunnelOverviewCroRuleSchema = z
  .object({
    ruleId: IdSchema,
    name: NonEmptyStringSchema,
    result: FunnelOverviewCroRuleResultSchema,
    weight: z.number().positive(),
    earnedScore: z.number().nonnegative(),
    maxScore: z.number().positive(),
    confidence: z.number().min(0).max(1),
    evidence: z.array(NonEmptyStringSchema),
    evidenceAssetIds: z.array(IdSchema).optional(),
    croLever: NonEmptyStringSchema.optional(),
    weightSource: NonEmptyStringSchema.optional(),
    pageSlug: NonEmptyStringSchema.optional(),
  })
  .passthrough();

export const FunnelOverviewCroCoverageSchema = z
  .object({
    applicableRules: z.number().int().nonnegative(),
    evaluatedRules: z.number().int().nonnegative(),
    notObservedRules: z.number().int().nonnegative(),
    percentage: z.number().min(0).max(100).nullable(),
  })
  .passthrough();

export const FunnelOverviewCroScorecardArtifactSchema = z
  .object({
    version: z.literal('2026-06-26'),
    producer: z.literal('tss'),
    analysisRunId: IdSchema,
    sourceUrl: NonEmptyStringSchema,
    generatedAt: TimestampSchema,
    status: FunnelOverviewCroScorecardStatusSchema,
    epistemicStatus: z.literal('hypothesis_seed'),
    terminalAction: z.literal('test_on_traffic'),
    score: z.number().min(0).max(100).nullable(),
    uncappedScore: z.number().min(0).max(100).nullable(),
    scoreCap: z.number().min(0).max(100).nullable(),
    grade: z.enum(['A', 'B', 'C', 'D']).nullable(),
    earnedScore: z.number().nonnegative(),
    maxScore: z.number().nonnegative(),
    applicableMaxScore: z.number().nonnegative(),
    coverage: FunnelOverviewCroCoverageSchema,
    rules: z.array(FunnelOverviewCroRuleSchema),
    spine: FunnelOverviewSpineAuditSchema.optional(),
    gaps: z.array(FunnelOverviewGapSchema),
    rubric: z
      .object({
        source: z.literal('BLOCK-FT-01'),
        frameworkDocumentStatus: z.enum(['available', 'missing']),
        frameworkVersion: NonEmptyStringSchema,
        weightsProvenance: z.enum([
          'codex_drafted',
          'owner_curated',
          'unavailable',
        ]),
        corpus: z
          .object({
            status: z.literal('expert_curated_exemplars'),
            n: z.number().int().nonnegative(),
            outcomeDataAttached: z.boolean(),
          })
          .passthrough(),
      })
      .passthrough(),
  })
  .passthrough()
  .superRefine((artifact, context) => {
    const scored = artifact.status === 'scored';
    const fields = [
      ['score', artifact.score],
      ['uncappedScore', artifact.uncappedScore],
      ['grade', artifact.grade],
    ] as const;

    for (const [field, value] of fields) {
      if (scored && value === null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `${field} is required when status is scored`,
        });
      }
      if (!scored && value !== null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: `${field} must be null when status is ${artifact.status}`,
        });
      }
    }
  });

export const FunnelOverviewRecommendationLensSchema = z.enum([
  'persuasion_psychology',
  'copywriting',
  'behavioral_economics',
  'cognitive_ux',
  'accessibility',
  'web_vitals',
  'analytics',
  'marketing_positioning',
]);

export const FunnelOverviewRecommendationSchema = z
  .object({
    id: IdSchema,
    findingRef: NonEmptyStringSchema,
    branch: FunnelOverviewRecommendationLensSchema,
    principle: NonEmptyStringSchema,
    intervention: NonEmptyStringSchema,
    why: NonEmptyStringSchema,
    priority: z.enum(['high', 'medium', 'low']),
    confidence: z.enum(['high', 'medium', 'low']),
    evidenceRefs: z.array(NonEmptyStringSchema).min(1),
    evidenceAssetIds: z.array(IdSchema).optional(),
    funnelContext: z
      .object({
        funnelType: NonEmptyStringSchema,
        frictionBand: NonEmptyStringSchema.optional(),
        pageSlug: NonEmptyStringSchema.optional(),
        gapField: NonEmptyStringSchema.optional(),
      })
      .passthrough(),
    source: z.enum(['structural_finding', 'gap']),
  })
  .passthrough();

export const FunnelOverviewRecommendationsArtifactSchema = z
  .object({
    version: z.literal('2026-06-20'),
    producer: z.literal('tss'),
    analysisRunId: IdSchema,
    sourceUrl: NonEmptyStringSchema,
    generatedAt: TimestampSchema,
    status: z.enum(['ready', 'no_grounded_recommendations']),
    recommendations: z.array(FunnelOverviewRecommendationSchema),
    gaps: z.array(FunnelOverviewGapSchema),
    surface: z
      .object({
        bundlePath: z.literal('analysis/recommendations.json'),
        futureChatCompatible: z.literal(true),
      })
      .passthrough(),
    guardrails: z
      .object({
        prescriptiveSeparateFromFunnelOverview: z.literal(true),
        requiresEvidenceAndNamedPrinciple: z.literal(true),
        noRecommendationWithoutDetectedGapOrFinding: z.literal(true),
      })
      .passthrough(),
  })
  .passthrough();

export const FunnelOverviewTemplateCustomizationBindingSchema = z
  .object({
    slot: IdSchema.optional(),
    slotId: IdSchema.optional(),
    slot_id: IdSchema.optional(),
    assetId: IdSchema.optional(),
    asset_id: IdSchema.optional(),
    value: JsonValueSchema.optional(),
    rank: z.number().nonnegative().optional(),
  })
  .passthrough();

export const FunnelOverviewTemplateCustomizationSchema = z
  .object({
    slots: z.array(FunnelOverviewTemplateCustomizationBindingSchema).default([]),
    proposalHash: IdSchema.optional(),
    proposal_hash: IdSchema.optional(),
  })
  .passthrough();

export const FunnelOverviewArtifactResponseSchema = z
  .object({
    version: NonEmptyStringSchema.optional(),
    schemaVersion: NonEmptyStringSchema.optional(),
    schema_version: NonEmptyStringSchema.optional(),
    status: LooseStatusSchema.optional(),
    sourceUrl: OptionalNullableStringSchema,
    source_url: OptionalNullableStringSchema,
    summary: funnelOverviewSummarySchema.optional(),
    brandObservation: FunnelOverviewBrandObservationSchema.optional(),
    evidenceMedia: z
      .array(FunnelOverviewEvidenceMediaBindingSchema)
      .optional(),
    domain: OptionalNullableStringSchema,
    url: OptionalNullableStringSchema,
    bundleDirName: NonEmptyStringSchema.optional(),
    primaryFunnelId: OptionalNullableStringSchema,
    funnels: z.array(FunnelOverviewObservedFunnelSchema).optional(),
    classification: FunnelOverviewClassificationSchema.optional(),
    fingerprint: JsonObjectSchema.optional(),
    friction: JsonObjectSchema.optional(),
    structuralAudit: FunnelOverviewStructuralAuditCompatibilitySchema.optional(),
    structural_audit: FunnelOverviewStructuralAuditCompatibilitySchema.optional(),
    templateCustomization: FunnelOverviewTemplateCustomizationSchema.optional(),
    template_customization: FunnelOverviewTemplateCustomizationSchema.optional(),
    assetManifest: z.array(FunnelOverviewAssetManifestEntrySchema).optional(),
    asset_manifest: z.array(FunnelOverviewAssetManifestEntrySchema).optional(),
    gaps: z
      .array(z.union([FunnelOverviewGapSchema, JsonValueSchema]))
      .optional(),
    quality: FunnelOverviewQualitySchema.optional(),
  })
  .merge(RunIdFieldsSchema)
  .merge(GeneratedAtFieldsSchema)
  .passthrough()
  .superRefine((artifact, context) => {
    const manifestAssets = [
      ...(artifact.assetManifest ?? []),
      ...(artifact.asset_manifest ?? []),
    ];
    const manifestAssetIds = new Set(
      manifestAssets.flatMap((asset) => [
        asset.id,
        ...(asset.assetId === undefined ? [] : [asset.assetId]),
        ...(asset.asset_id === undefined ? [] : [asset.asset_id]),
      ]),
    );
    for (const [index, binding] of (artifact.evidenceMedia ?? []).entries()) {
      if (!manifestAssetIds.has(binding.assetId)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['evidenceMedia', index, 'assetId'],
          message: 'visual evidence must reference an asset in assetManifest',
        });
      }
    }

    const summaryBrand = artifact.summary?.brand?.trim().toLowerCase();
    const observedBrand = artifact.brandObservation?.name.trim().toLowerCase();
    if (
      summaryBrand !== undefined &&
      observedBrand !== undefined &&
      summaryBrand !== observedBrand
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['summary', 'brand'],
        message: 'summary.brand must mirror brandObservation.name during transition',
      });
    }
  });

export const FunnelOverviewAssetLicenseVerdictRequestSchema = z
  .object({
    verdict: FunnelOverviewLicenseVerdictSchema,
    reason: NonEmptyStringSchema,
  })
  .merge(HumanApprovalFieldsSchema)
  .passthrough();

export const FunnelOverviewAssetLicenseVerdictResponseSchema = z
  .object({
    status: LooseStatusSchema.optional(),
    assetId: IdSchema.optional(),
    asset_id: IdSchema.optional(),
    verdict: FunnelOverviewLicenseVerdictSchema.optional(),
    asset: FunnelOverviewAssetManifestEntrySchema.optional(),
    fichaHash: NonEmptyStringSchema.optional(),
    ficha_hash: NonEmptyStringSchema.optional(),
    updatedAt: TimestampSchema.optional(),
    updated_at: TimestampSchema.optional(),
  })
  .merge(RunIdFieldsSchema)
  .merge(HumanApprovalFieldsSchema)
  .passthrough();

export const FunnelOverviewTemplateCustomizationProposeRequestSchema = z
  .object({
    mode: z.literal('propose'),
    slots: z.array(JsonValueSchema),
  })
  .passthrough();

export const FunnelOverviewTemplateCustomizationProposeResponseSchema = z
  .object({
    diff: JsonValueSchema.optional(),
    errors: z.array(JsonValueSchema).default([]),
    bindings: z
      .array(FunnelOverviewTemplateCustomizationBindingSchema)
      .optional(),
  })
  .merge(RunIdFieldsSchema)
  .merge(ProposalHashFieldsSchema)
  .passthrough();

export const FunnelOverviewTemplateCustomizationApplyRequestSchema = z.union([
  z
    .object({
      mode: z.literal('apply'),
      proposalHash: IdSchema,
    })
    .passthrough(),
  z
    .object({
      mode: z.literal('apply'),
      proposal_hash: IdSchema,
    })
    .passthrough(),
]);

export const FunnelOverviewTemplateCustomizationApplyResponseSchema = z
  .object({
    status: LooseStatusSchema,
    appliedAt: TimestampSchema.optional(),
    applied_at: TimestampSchema.optional(),
    errors: z.array(JsonValueSchema).optional(),
  })
  .merge(RunIdFieldsSchema)
  .merge(ProposalHashFieldsSchema)
  .passthrough();

export const FunnelOverviewTemplateCustomizationRequestSchema = z.union([
  FunnelOverviewTemplateCustomizationProposeRequestSchema,
  FunnelOverviewTemplateCustomizationApplyRequestSchema,
]);

export const FunnelOverviewTemplateBuildRequestSchema = z
  .object({
    templateDir: NonEmptyStringSchema.optional(),
    template_dir: NonEmptyStringSchema.optional(),
    outputDir: NonEmptyStringSchema.optional(),
    output_dir: NonEmptyStringSchema.optional(),
    install: z.boolean().optional(),
    build: z.boolean().optional(),
  })
  .passthrough();

export const FunnelOverviewTemplateBuildResponseSchema = z
  .object({
    status: LooseStatusSchema,
    streamUrl: NonEmptyStringSchema.optional(),
    stream_url: NonEmptyStringSchema.optional(),
  })
  .merge(RunIdFieldsSchema)
  .merge(BuildIdFieldsSchema)
  .passthrough();

export const FunnelOverviewTemplateBuildSseEventPayloadSchema = z
  .object({
    type: NonEmptyStringSchema.optional(),
    status: LooseStatusSchema.optional(),
    step: NonEmptyStringSchema.optional(),
    path: NonEmptyStringSchema.optional(),
    message: z.string().optional(),
    error: z.string().optional(),
    progress: z.number().min(0).max(100).optional(),
  })
  .merge(RunIdFieldsSchema)
  .merge(BuildIdFieldsSchema)
  .passthrough();

export const FunnelOverviewTemplateBuildSseEventSchema = z
  .object({
    event: NonEmptyStringSchema,
    data: FunnelOverviewTemplateBuildSseEventPayloadSchema,
  })
  .passthrough();

export const FunnelOverviewAnalysisRunProgressSseEventPayloadSchema = z
  .object({
    type: NonEmptyStringSchema,
    status: LooseStatusSchema.optional(),
    slug: NonEmptyStringSchema.optional(),
    url: NonEmptyStringSchema.optional(),
    bundleUrl: NonEmptyStringSchema.optional(),
    bundle_url: NonEmptyStringSchema.optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
    size_bytes: z.number().int().nonnegative().optional(),
    pagesCaptured: z.number().int().nonnegative().optional(),
    pages_captured: z.number().int().nonnegative().optional(),
    maxPages: z.number().int().positive().optional(),
    max_pages: z.number().int().positive().optional(),
    extractionId: IdSchema.optional(),
    extraction_id: IdSchema.optional(),
    extractorsPassed: z.number().int().nonnegative().optional(),
    extractors_passed: z.number().int().nonnegative().optional(),
    extractorsTotal: z.number().int().nonnegative().optional(),
    extractors_total: z.number().int().nonnegative().optional(),
    score: z.number().optional(),
    reason: z.string().optional(),
    diagnostics: z.array(z.string()).optional(),
    blockers: z.array(z.string()).optional(),
  })
  .merge(RunIdFieldsSchema)
  .passthrough();

export const FunnelOverviewAnalysisRunProgressSseEventSchema = z
  .object({
    event: NonEmptyStringSchema,
    data: FunnelOverviewAnalysisRunProgressSseEventPayloadSchema,
  })
  .passthrough();

export type FunnelOverviewLicenseVerdict = z.infer<
  typeof FunnelOverviewLicenseVerdictSchema
>;
export type FunnelOverviewAssetBindingMode = z.infer<
  typeof FunnelOverviewAssetBindingModeSchema
>;
export type FunnelOverviewBrandResolution = z.infer<
  typeof FunnelOverviewBrandResolutionSchema
>;
export type FunnelOverviewBrandSource = z.infer<
  typeof FunnelOverviewBrandSourceSchema
>;
export type FunnelOverviewBrandObservation = z.infer<
  typeof FunnelOverviewBrandObservationSchema
>;
export type FunnelOverviewEvidenceMediaKind = z.infer<
  typeof FunnelOverviewEvidenceMediaKindSchema
>;
export type FunnelOverviewEvidenceMediaBinding = z.infer<
  typeof FunnelOverviewEvidenceMediaBindingSchema
>;
export type FunnelOverviewAssetManifestEntry = z.infer<
  typeof FunnelOverviewAssetManifestEntrySchema
>;
export type FunnelOverviewFrameworkType = z.infer<
  typeof FunnelOverviewFrameworkTypeSchema
>;
export type FunnelOverviewFrameworkRule = z.infer<
  typeof FunnelOverviewFrameworkRuleSchema
>;
export type FunnelOverviewFrameworkRegistryResponse = z.infer<
  typeof FunnelOverviewFrameworkRegistryResponseSchema
>;
export type FunnelOverviewTemplateCustomizationSlot = z.infer<
  typeof FunnelOverviewTemplateCustomizationSlotSchema
>;
export type FunnelOverviewTemplateCustomizationSlotRegistryResponse = z.infer<
  typeof FunnelOverviewTemplateCustomizationSlotRegistryResponseSchema
>;
export type FunnelOverviewSeedProduct = z.infer<
  typeof FunnelOverviewSeedProductSchema
>;
export type FunnelOverviewSeedCollection = z.infer<
  typeof FunnelOverviewSeedCollectionSchema
>;
export type FunnelOverviewSeedCart = z.infer<
  typeof FunnelOverviewSeedCartSchema
>;
export type FunnelOverviewSeedFunnelStep = z.infer<
  typeof FunnelOverviewSeedFunnelStepSchema
>;
export type FunnelOverviewSeedFunnel = z.infer<
  typeof FunnelOverviewSeedFunnelSchema
>;
export type FunnelOverviewSeedFunnels = z.infer<
  typeof FunnelOverviewSeedFunnelsSchema
>;
export type FunnelOverviewSeedOffer = z.infer<
  typeof FunnelOverviewSeedOfferSchema
>;
export type FunnelOverviewSeedArtifacts = z.infer<
  typeof FunnelOverviewSeedArtifactsSchema
>;
export type FunnelOverviewSeedsResponse = z.infer<
  typeof FunnelOverviewSeedsResponseSchema
>;
export type FunnelOverviewGap = z.infer<typeof FunnelOverviewGapSchema>;
export type FunnelOverviewClassification = z.infer<
  typeof FunnelOverviewClassificationSchema
>;
export type FunnelOverviewObservedFunnel = z.infer<
  typeof FunnelOverviewObservedFunnelSchema
>;
export type FunnelOverviewQuality = z.infer<
  typeof FunnelOverviewQualitySchema
>;
export type FunnelOverviewSpineComponentRef = z.infer<
  typeof FunnelOverviewSpineComponentRefSchema
>;
export type FunnelOverviewSpineWinnerCombination = z.infer<
  typeof FunnelOverviewSpineWinnerCombinationSchema
>;
export type FunnelOverviewSpineAudit = z.infer<
  typeof FunnelOverviewSpineAuditSchema
>;
export type FunnelOverviewStructuralAuditFinding = z.infer<
  typeof FunnelOverviewStructuralAuditFindingSchema
>;
export type FunnelOverviewStructuralAudit = z.infer<
  typeof FunnelOverviewStructuralAuditSchema
>;
export type FunnelOverviewCroScorecardStatus = z.infer<
  typeof FunnelOverviewCroScorecardStatusSchema
>;
export type FunnelOverviewCroRuleResult = z.infer<
  typeof FunnelOverviewCroRuleResultSchema
>;
export type FunnelOverviewCroRule = z.infer<
  typeof FunnelOverviewCroRuleSchema
>;
export type FunnelOverviewCroCoverage = z.infer<
  typeof FunnelOverviewCroCoverageSchema
>;
export type FunnelOverviewCroScorecardArtifact = z.infer<
  typeof FunnelOverviewCroScorecardArtifactSchema
>;
export type FunnelOverviewRecommendationLens = z.infer<
  typeof FunnelOverviewRecommendationLensSchema
>;
export type FunnelOverviewRecommendation = z.infer<
  typeof FunnelOverviewRecommendationSchema
>;
export type FunnelOverviewRecommendationsArtifact = z.infer<
  typeof FunnelOverviewRecommendationsArtifactSchema
>;
export type FunnelOverviewTemplateCustomizationBinding = z.infer<
  typeof FunnelOverviewTemplateCustomizationBindingSchema
>;
export type FunnelOverviewTemplateCustomization = z.infer<
  typeof FunnelOverviewTemplateCustomizationSchema
>;
export type FunnelOverviewArtifactResponse = z.infer<
  typeof FunnelOverviewArtifactResponseSchema
>;
export type FunnelOverviewAssetLicenseVerdictRequest = z.infer<
  typeof FunnelOverviewAssetLicenseVerdictRequestSchema
>;
export type FunnelOverviewAssetLicenseVerdictResponse = z.infer<
  typeof FunnelOverviewAssetLicenseVerdictResponseSchema
>;
export type FunnelOverviewTemplateCustomizationProposeRequest = z.infer<
  typeof FunnelOverviewTemplateCustomizationProposeRequestSchema
>;
export type FunnelOverviewTemplateCustomizationProposeResponse = z.infer<
  typeof FunnelOverviewTemplateCustomizationProposeResponseSchema
>;
export type FunnelOverviewTemplateCustomizationApplyRequest = z.infer<
  typeof FunnelOverviewTemplateCustomizationApplyRequestSchema
>;
export type FunnelOverviewTemplateCustomizationApplyResponse = z.infer<
  typeof FunnelOverviewTemplateCustomizationApplyResponseSchema
>;
export type FunnelOverviewTemplateCustomizationRequest = z.infer<
  typeof FunnelOverviewTemplateCustomizationRequestSchema
>;
export type FunnelOverviewTemplateBuildRequest = z.infer<
  typeof FunnelOverviewTemplateBuildRequestSchema
>;
export type FunnelOverviewTemplateBuildResponse = z.infer<
  typeof FunnelOverviewTemplateBuildResponseSchema
>;
export type FunnelOverviewTemplateBuildSseEventPayload = z.infer<
  typeof FunnelOverviewTemplateBuildSseEventPayloadSchema
>;
export type FunnelOverviewTemplateBuildSseEvent = z.infer<
  typeof FunnelOverviewTemplateBuildSseEventSchema
>;
export type FunnelOverviewAnalysisRunProgressSseEventPayload = z.infer<
  typeof FunnelOverviewAnalysisRunProgressSseEventPayloadSchema
>;
export type FunnelOverviewAnalysisRunProgressSseEvent = z.infer<
  typeof FunnelOverviewAnalysisRunProgressSseEventSchema
>;
